import * as React from 'react';
import './subscribers-barchart.scss';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'core/redux/store';
import { Overview } from 'core/models/overview/overview';
import axios from 'axios';
import { useRef, useState } from 'react';
import { ApiResult } from 'apis/apiresult';
import { Bar } from 'react-chartjs-2';
import BarChart from 'components/chart/chart';
import { BarElement, CategoryScale, Chart, LinearScale, BarController, Tooltip } from 'chart.js';
import { toShamsi, toShamsiDate } from 'utils/scripts/date';
import { Order } from 'core/domains/order';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Subscriber } from 'core/domains/subscriber';
import { subscribe } from 'diagnostics_channel';

Chart.register(LinearScale, CategoryScale, BarController, BarElement, Tooltip);

const mapState = (state: RootState) => ({
    state: state,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
    data: Subscriber[];
    title: string;
    startDate: string,
    endDate: string
}


const SubscribersBarChart: React.FC<Props> = (props) => {
    const [chartData, setChartData] = useState<any>({});

    const chartRef = useRef<HTMLCanvasElement | null>(null);
    const chartInstanceRef = useRef<Chart | null>(null);

    // State to hold the fetched data
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    React.useEffect(() => {
        // Process the orders to group by day
        const groupedData: { [key: string]: number } = {};

        const removeFirstKeyValuePairImmutable = (obj: { [key: string]: number }) => {
            const keys = Object.keys(obj);
            if (keys.length > 0) {
                const { [keys[0]]: _, ...newObj } = obj; // Create a new object without the first key
                return newObj;
            }
            return obj; // Return the original if no keys
        };


        props.data && props.data.length > 0 && props.data.forEach(subscriber => {
            var date = new Date(subscriber.joinedOn).toISOString().split('T')[0];
            date = toShamsiDate(date); // Get the date part (YYYY-MM-DD)
            if (!groupedData[date]) {
                groupedData[date] = 0; // Initialize if not present
            }
            groupedData[date] += 1; // Sum the amounts for the same date
        });

        // Prepare data for the chart
        const labels = Object.keys(removeFirstKeyValuePairImmutable(groupedData));
        const items = Object.values(removeFirstKeyValuePairImmutable(groupedData));

        // Destroy the previous chart instance if it exists
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }

        // Create a new chart instance
        const ctx = chartRef.current?.getContext('2d');
        if (ctx) {
            chartInstanceRef.current = new Chart(ctx, {
                type: 'line',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: props.title,
                            data: items,
                            backgroundColor: 'rgba(128, 63, 95, 0.6)',
                            borderColor: 'rgba(128, 63, 95, 1)',
                            borderWidth: 1,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    plugins: {
                        legend: {
                            position: 'top',
                            labels: {
                                font: {
                                    family: 'YekanBakh', // Set font family for legend
                                    size: 14, // Set font size for legend
                                },
                            },
                        },
                        title: {
                            display: true,
                            text: 'کاربران',
                            font: {
                                family: 'YekanBakh', // Set font family for legend
                                size: 16, // Set font size for title
                                weight: 'bold', // Set font weight for title
                            },
                        },
                        tooltip: {
                            mode: 'index',
                            intersect: false,
                            titleFont: {
                                family: 'YekanBakh', // Set font family for legend
                                size: 14, // Set font size for tooltip title
                            },
                            bodyFont: {
                                family: 'YekanBakh', // Set font family for legend
                                size: 12, // Set font size for tooltip body
                            },
                            callbacks: {
                                // Custom tooltip label
                                label: function (tooltipItem) {
                                    const amount = tooltipItem.raw as number; // Get the raw data value
                                    return `مجموع کاربر : ${amount.toLocaleString()}`; // Format with commas
                                },
                            },
                        }
                    },
                    scales: {
                        x: {
                            type: 'category', // Use category scale for x-axis
                            title: {
                                display: false,
                                text: 'روز',
                                font: {
                                    family: 'YekanBakh', // Set font family for x-axis title
                                    size: 12,
                                    style: 'normal',
                                    weight: 'normal'
                                }
                            },
                            ticks: {
                                display: false,

                                font: {
                                    family: 'YekanBakh', // Set font family for x-axis ticks
                                    size: 10,
                                    style: 'normal',
                                    weight: 'normal'
                                }
                            }
                        },
                        y: {
                            type: 'linear', // Use linear scale for y-axis
                            beginAtZero: true,
                            title: {
                                display: true,
                                text: 'کاربر',
                                font: {
                                    family: 'YekanBakh', // Set font family for x-axis title
                                    size: 12,
                                    style: 'normal',
                                    weight: 'normal'
                                }
                            },
                            ticks: {
                                font: {
                                    family: 'YekanBakh', // Set font family for x-axis ticks
                                    size: 10,
                                    style: 'normal',
                                    weight: 'normal'
                                }
                            }
                        },
                    },
                },
            });
        }

        // Cleanup function to destroy the chart instance on component unmount
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, [props.data]); // Re-run effect if orders change


    return (
        <>
            <div className="overview-container">
                <div className="container">
                    {
                        <canvas ref={chartRef} />
                    }
                </div>
            </div>
        </>
    );
};

export default connector(SubscribersBarChart);
